import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Text,
  Autocomplete,
  Select,
  MultiSelect,
  ActionIcon,
  Badge,
  Box,
  Group,
  Stack,
  Tooltip,
  Alert,
  Button,
  TextInput,
  Kbd,
  VisuallyHidden,
} from "@mantine/core";
import {
  IconSearch,
  IconFlask,
  IconAlertTriangle,
  IconAlertCircle,
  IconTrash,
  IconPlus,
  IconRestore,
} from "@tabler/icons-react";
import RichTextDiagnosis from "./RichTextDiagnosis";
import {
  filterIcdCodeOptions,
  IcdCodeSelectOption,
  type IcdCodeComboboxItem,
} from "./IcdCodeSelectOption";
import { useSubsites } from "../../hooks/useReferenceData";
import { isEmptyHtml } from "../../utils/helpers";
import { resolveVariableChipValues } from "../shared/tiptap/VariableChip";

interface SpecimenDiagnosisData {
  specimenId: string;
  macroCode: string;
  diagnosisText: string;
  microscopicDescription: string;
  location: string | null;
  category: string | null;
  cptCode: string | null;
  icdCode: string | null;
  icdCodes: string[];
  color: string | null;
  sizeCm: string | null;
  sizeMm: string | null;
  consistency: string | null;
}

interface DiagnosisMacro {
  id: string;
  code: string;
  label: string;
  diagnosisText: string;
  microscopicDescription: string | null;
  category: string | null;
  cptCode: string | null;
  icdCode: string | null;
  icdCodes?: string[];
  linkedStains?: Array<{
    id: string;
    code: string;
    description: string;
    stainType: string;
  }>;
}

interface CaseInfo {
  caseNumber?: string | null;
  patientName?: string | null;
  dateCollected?: string | null;
}

interface DiagnosisEntryGridProps {
  specimens: any[];
  diagnosisDataMap: Map<string, SpecimenDiagnosisData>;
  onDiagnosisChange: (
    specimenId: string,
    data: Partial<SpecimenDiagnosisData>
  ) => void;
  onOpenMacroSearch: (specimenId: string) => void;
  diagnosisMacros: DiagnosisMacro[];
  categoryOptions: Array<{ value: string; label: string }>;
  icdCodeOptions: Array<{ value: string; label: string }>;
  isReadOnly?: boolean;
  caseInfo?: CaseInfo;
  // Structural edits (add, remove, recover) are permission-gated, independent
  // of the diagnosis read-only state.
  canModifyStructure?: boolean;
  canAddSpecimen?: boolean;
  onArchiveSpecimen?: (specimenId: string, specimenNumber: string) => void;
  onAddSpecimen?: () => void;
  onRecoverSpecimens?: () => void;
  archivedCount?: number;
  onOpenMacroSearchBar?: () => void;
  canRequestAdditionalStains?: boolean;
  onRequestStains?: (specimenId: string) => void;
}

// Rows mount before Mantine attaches the Autocomplete input ref, so initial
// focus retries across a bounded number of animation frames before giving up.
const INITIAL_FOCUS_RETRY_FRAMES = 20;

// Strips HTML tags to plain text for row preview
function stripHtml(html: string): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

// Strips HTML and removes {{VARIABLE}} placeholders for diagnosis text search
function stripVariablesAndHtml(text: string): string {
  if (!text) return "";
  const withoutVars = text.replace(/\{\{[^}]+\}\}/g, "");
  return stripHtml(withoutVars).trim();
}

// Replaces {{VARIABLE}} tokens with their resolved values for cell preview display.
// Falls back to the variable name if no value is available.
function resolveVariablesForPreview(
  text: string,
  vars: Record<string, string | null | undefined>
): string {
  if (!text) return text;
  return text.replace(/\{\{(\w+)\}\}/g, (_, name) => {
    const val = vars[name];
    return val?.trim() ? val.trim() : `{{${name}}}`;
  });
}

/** A diagnosis satisfies the ICD requirement with either the ordered list or the legacy single code */
function hasAnyIcdCode(diagnosisData: SpecimenDiagnosisData): boolean {
  return Boolean(diagnosisData.icdCodes?.length || diagnosisData.icdCode);
}

/** Full ordered ICD list, falling back to the legacy single code for unmigrated rows */
function resolveIcdCodes(diagnosisData: SpecimenDiagnosisData): string[] {
  if (diagnosisData.icdCodes?.length) return diagnosisData.icdCodes;
  return diagnosisData.icdCode ? [diagnosisData.icdCode] : [];
}

/** Returns list of missing required field labels for a specimen's diagnosis data */
function getMissingFields(diagnosisData: SpecimenDiagnosisData): string[] {
  const missing: string[] = [];
  if (isEmptyHtml(diagnosisData.diagnosisText)) missing.push("Diagnosis");
  if (!diagnosisData.category) missing.push("Category");
  if (!diagnosisData.cptCode) missing.push("CPT Code");
  if (!hasAnyIcdCode(diagnosisData)) missing.push("ICD Code");
  return missing;
}

interface RowState {
  macroInput: string;
  macroError: boolean;
}

function DiagnosisRow({
  specimen,
  diagnosisData,
  inputRef,
  onDiagnosisChange,
  onOpenMacroSearch,
  diagnosisMacros,
  categoryOptions,
  icdSelectOptions,
  isReadOnly,
  columnCount,
  onFocusNextMacro,
  caseInfo,
  canModifyStructure,
  canRemove,
  onArchiveSpecimen,
  canRequestAdditionalStains,
  onRequestStains,
}: {
  specimen: any;
  diagnosisData: SpecimenDiagnosisData;
  inputRef: React.RefObject<HTMLInputElement>;
  onDiagnosisChange: (data: Partial<SpecimenDiagnosisData>) => void;
  onOpenMacroSearch: () => void;
  diagnosisMacros: DiagnosisMacro[];
  categoryOptions: Array<{ value: string; label: string }>;
  icdSelectOptions: IcdCodeComboboxItem[];
  isReadOnly?: boolean;
  columnCount: number;
  onFocusNextMacro?: () => void;
  caseInfo?: CaseInfo;
  canModifyStructure?: boolean;
  canRemove?: boolean;
  onArchiveSpecimen?: (specimenId: string, specimenNumber: string) => void;
  canRequestAdditionalStains?: boolean;
  onRequestStains?: (specimenId: string) => void;
}) {
  const [rowState, setRowState] = useState<RowState>({
    macroInput: diagnosisData.macroCode || "",
    macroError: false,
  });
  const [expandedField, setExpandedField] = useState<
    "diagnosis" | "microscopic" | null
  >(null);
  const expansionRowRef = useRef<HTMLTableRowElement>(null);
  const dataRowRef = useRef<HTMLTableRowElement>(null);

  // Fetch available subsites for the specimen's source to populate LOCATION dropdown
  const { data: subsiteOptions } = useSubsites(specimen?.sourceId);

  const handleRemoveSpecimen = useCallback(() => {
    onArchiveSpecimen?.(specimen.id, specimen.number);
  }, [onArchiveSpecimen, specimen.id, specimen.number]);

  const handleIcdCodesChange = useCallback(
    (codes: string[]) =>
      onDiagnosisChange({ icdCodes: codes, icdCode: codes[0] || null }),
    [onDiagnosisChange]
  );

  const handleCategoryChange = useCallback(
    (category: string | null) => onDiagnosisChange({ category }),
    [onDiagnosisChange]
  );

  const handleRequestStains = useCallback(() => {
    onRequestStains?.(specimen.id);
  }, [onRequestStains, specimen.id]);

  // Close expanded field when clicking outside both the data row and expansion row
  useEffect(() => {
    if (!expandedField) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest?.(".mantine-Menu-dropdown") ||
        target.closest?.(".mantine-Popover-dropdown") ||
        target.closest?.("[data-text-expansion-popup]")
      ) {
        return;
      }
      if (
        expansionRowRef.current &&
        !expansionRowRef.current.contains(target) &&
        dataRowRef.current &&
        !dataRowRef.current.contains(target)
      ) {
        setExpandedField(null);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [expandedField]);

  // Sync macroInput when diagnosisData.macroCode changes from outside (e.g. initial
  // load, refresh, or copy from another row). useEffect is used instead of an
  // in-render ref mutation to be safe in React 18 concurrent mode.
  useEffect(() => {
    setRowState((s) => {
      if (s.macroInput !== (diagnosisData.macroCode || "")) {
        return {
          ...s,
          macroInput: diagnosisData.macroCode || "",
          macroError: false,
        };
      }
      return s;
    });
  }, [diagnosisData.macroCode]);

  // Shared macro application — used by both Tab/Enter and autocomplete option click
  const applyMacro = (macro: DiagnosisMacro) => {
    setRowState((s) => ({ ...s, macroInput: macro.code, macroError: false }));
    const macroIcdCodes = macro.icdCodes?.length
      ? macro.icdCodes
      : macro.icdCode
      ? [macro.icdCode]
      : [];
    onDiagnosisChange({
      macroCode: macro.code,
      diagnosisText: macro.diagnosisText || "",
      microscopicDescription: macro.microscopicDescription || "",
      category: macro.category || null,
      cptCode: macro.cptCode || null,
      icdCode: macroIcdCodes[0] || null,
      icdCodes: macroIcdCodes,
    });
  };

  const expandMacro = (code: string): boolean => {
    if (!code.trim()) {
      setRowState((s) => ({ ...s, macroError: false }));
      return false;
    }
    const macro = diagnosisMacros.find(
      (m) => m.code.toLowerCase() === code.toLowerCase()
    );
    if (!macro) {
      setRowState((s) => ({ ...s, macroError: true }));
      return false;
    }
    applyMacro(macro);
    return true;
  };

  const handleMacroKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "f") {
      e.preventDefault();
      onOpenMacroSearch();
      return;
    }
    if (e.key === "Tab" || e.key === "Enter") {
      const expanded = expandMacro(rowState.macroInput);
      if (expanded) {
        e.preventDefault();
        setTimeout(() => onFocusNextMacro?.(), 0);
      } else if (e.key === "Enter") {
        e.preventDefault();
      }
    }
  };

  const handleMacroBlur = () => {
    if (
      rowState.macroInput &&
      rowState.macroInput !== diagnosisData.macroCode
    ) {
      expandMacro(rowState.macroInput);
    }
  };

  // Called when user clicks an autocomplete suggestion
  const handleOptionSubmit = (value: string) => {
    const macro = diagnosisMacros.find(
      (m) => m.code.toLowerCase() === value.toLowerCase()
    );
    if (macro) {
      applyMacro(macro);
      setTimeout(() => onFocusNextMacro?.(), 0);
    }
  };

  // Filter macros by code match first. When 4+ characters are typed, also search
  // diagnosis text and microscopic description.
  // Ranking: exact code (0) > code starts-with (1) > code contains (2) >
  //          diagnosis text (3, 4+ chars only) > microscopic desc (4, 4+ chars only).
  const macroSuggestions = useMemo(() => {
    const query = rowState.macroInput.trim().toLowerCase();
    if (!query) return [];

    const searchText = query.length >= 4;

    return diagnosisMacros
      .reduce<Array<{ macro: DiagnosisMacro; rank: number }>>((acc, m) => {
        const codeLC = m.code.toLowerCase();
        if (codeLC === query) {
          acc.push({ macro: m, rank: 0 });
        } else if (codeLC.startsWith(query)) {
          acc.push({ macro: m, rank: 1 });
        } else if (codeLC.includes(query)) {
          acc.push({ macro: m, rank: 2 });
        } else if (searchText) {
          const plainDiagnosis = stripVariablesAndHtml(m.diagnosisText);
          const plainMicroscopic = stripVariablesAndHtml(
            m.microscopicDescription || ""
          );
          if (plainDiagnosis.toLowerCase().includes(query)) {
            acc.push({ macro: m, rank: 3 });
          } else if (plainMicroscopic.toLowerCase().includes(query)) {
            acc.push({ macro: m, rank: 4 });
          }
        }
        return acc;
      }, [])
      .sort((a, b) => a.rank - b.rank)
      .slice(0, 15)
      .map(({ macro: m }) => ({ value: m.code, label: m.code }));
  }, [rowState.macroInput, diagnosisMacros]);

  const missingFields = getMissingFields(diagnosisData);
  const isComplete = missingFields.length === 0;
  const hasPartial =
    !isEmptyHtml(diagnosisData.diagnosisText) ||
    !!diagnosisData.category ||
    !!diagnosisData.cptCode ||
    hasAnyIcdCode(diagnosisData);

  const hasMissingDiagnosis =
    hasPartial && isEmptyHtml(diagnosisData.diagnosisText);
  const statusColor = isComplete
    ? "green"
    : hasMissingDiagnosis
    ? "red"
    : hasPartial
    ? "orange"
    : "gray";

  // diagnosisData.location stores the label (display name) chosen from the subsite dropdown
  const locationLabel =
    diagnosisData.location ?? specimen.subsite?.name ?? null;

  // Build resolved variable map for cell preview
  const resolvedVars: Record<string, string | null | undefined> = {
    specimen_letter: specimen.number ?? null,
    source: specimen.source?.name ?? null,
    subsite: locationLabel,
    collection_method: specimen.collectionMethod ?? null,
    block_count:
      specimen.blocks?.length != null ? String(specimen.blocks.length) : null,
    case_number: caseInfo?.caseNumber ?? null,
    patient_name: caseInfo?.patientName ?? null,
    date_collected: caseInfo?.dateCollected ?? null,
    color: diagnosisData.color ?? null,
    size_cm: diagnosisData.sizeCm ?? null,
    sizeCm: diagnosisData.sizeCm ?? null,
    size_mm: diagnosisData.sizeMm ?? null,
    consistency: diagnosisData.consistency ?? null,
    LOCATION: locationLabel,
    location: locationLabel,
  };

  const diagnosisPlain = stripHtml(
    resolveVariablesForPreview(
      resolveVariableChipValues(diagnosisData.diagnosisText, resolvedVars),
      resolvedVars
    )
  );
  const microscopicPlain = stripHtml(
    resolveVariablesForPreview(
      resolveVariableChipValues(
        diagnosisData.microscopicDescription,
        resolvedVars
      ),
      resolvedVars
    )
  );

  const categoryLabel =
    categoryOptions.find((o) => o.value === diagnosisData.category)?.label ||
    null;

  const canRequestStains = useMemo(
    () =>
      !!canRequestAdditionalStains &&
      !!onRequestStains &&
      (specimen.blocks?.length ?? 0) > 0,
    [canRequestAdditionalStains, onRequestStains, specimen.blocks?.length]
  );

  const handleDiagnosisCellClick = () => {
    if (isReadOnly) return;
    setExpandedField((f) => (f === "diagnosis" ? null : "diagnosis"));
  };

  const handleMicroscopicCellClick = () => {
    if (isReadOnly) return;
    setExpandedField((f) => (f === "microscopic" ? null : "microscopic"));
  };

  return (
    <>
      <Table.Tr ref={dataRowRef}>
        {/* Specimen */}
        <Table.Td style={{ width: 160, verticalAlign: "top", paddingTop: 8 }}>
          <Stack gap={4} align="flex-start">
            <Tooltip
              label={
                isComplete
                  ? "Complete"
                  : hasMissingDiagnosis
                  ? `Missing: Diagnosis`
                  : hasPartial
                  ? `No ${missingFields.join(", no ")}`
                  : "No diagnosis entered"
              }
              openDelay={200}
              withArrow
              multiline
              maw={300}
            >
              <Badge
                color={statusColor}
                variant="filled"
                size="md"
                style={{ fontFamily: "monospace", cursor: "default" }}
              >
                {specimen.number}
              </Badge>
            </Tooltip>
            {specimen.source?.name && (
              <Text size="xs" fw={600} truncate style={{ maxWidth: "100%" }}>
                {specimen.source.name}
              </Text>
            )}
            {specimen.subsite?.name && (
              <Text size="xs" c="dimmed" truncate style={{ maxWidth: "100%" }}>
                {specimen.subsite.name}
              </Text>
            )}
            {specimen.findings && (
              <Tooltip
                label={specimen.findings}
                openDelay={300}
                withArrow
                multiline
                maw={260}
                disabled={specimen.findings.length <= 60}
              >
                <Text
                  size="xs"
                  c="dimmed"
                  lineClamp={2}
                  style={{
                    cursor: specimen.findings.length > 60 ? "help" : "default",
                    maxWidth: "100%",
                  }}
                >
                  {specimen.findings}
                </Text>
              </Tooltip>
            )}
          </Stack>
        </Table.Td>

        {/* Dx Code — autocomplete with code prefix + diagnosis text search */}
        <Table.Td style={{ width: 110, verticalAlign: "middle" }}>
          <Autocomplete
            ref={inputRef}
            value={rowState.macroInput}
            onChange={(val) =>
              setRowState((s) => ({
                ...s,
                macroInput: val.toUpperCase(),
                macroError: false,
              }))
            }
            onKeyDown={handleMacroKeyDown}
            onBlur={handleMacroBlur}
            onOptionSubmit={handleOptionSubmit}
            data={macroSuggestions}
            filter={({ options }) => options}
            renderOption={({ option }) => {
              const macro = diagnosisMacros.find(
                (m) => m.code === option.value
              );
              const preview = macro
                ? stripVariablesAndHtml(macro.diagnosisText)
                : "";
              const stainCount = macro?.linkedStains?.length ?? 0;
              return (
                <Group gap="xs" wrap="nowrap">
                  <Text
                    size="xs"
                    fw={700}
                    ff="monospace"
                    style={{ flexShrink: 0 }}
                  >
                    {option.value}
                  </Text>
                  {preview && (
                    <Text
                      size="xs"
                      c="dimmed"
                      lineClamp={1}
                      style={{ flex: 1, overflow: "hidden" }}
                    >
                      {preview}
                    </Text>
                  )}
                  {stainCount > 0 && (
                    <Badge
                      size="xs"
                      variant="light"
                      color="teal"
                      style={{ flexShrink: 0 }}
                    >
                      {stainCount} stain{stainCount !== 1 ? "s" : ""}
                    </Badge>
                  )}
                </Group>
              );
            }}
            comboboxProps={{ width: 450, position: "bottom-start" }}
            placeholder="Code"
            disabled={isReadOnly}
            error={rowState.macroError ? "Not found" : undefined}
            size="xs"
            styles={{
              input: {
                fontFamily: "monospace",
                fontWeight: 600,
                textTransform: "uppercase",
              },
            }}
            rightSection={
              // Kept out of the tab sequence: tabbing along a row should reach
              // the next data field, not this mouse shortcut. Ctrl+F opens the
              // same search from inside the field.
              <ActionIcon
                size="xs"
                variant="subtle"
                color="gray"
                onClick={onOpenMacroSearch}
                title="Search macros (Ctrl+F)"
                disabled={isReadOnly}
                tabIndex={-1}
              >
                <IconSearch size={11} />
              </ActionIcon>
            }
            rightSectionPointerEvents="all"
          />
          {/* Auto-linked stains indicator */}
          {(() => {
            const appliedMacro = diagnosisData.macroCode
              ? diagnosisMacros.find(
                  (m) =>
                    m.code.toLowerCase() ===
                    diagnosisData.macroCode.toLowerCase()
                )
              : null;
            const count = appliedMacro?.linkedStains?.length ?? 0;
            if (count === 0) return null;
            const stainLabels = appliedMacro!
              .linkedStains!.map((s) => s.code)
              .join(", ");
            return (
              <Tooltip
                label={`Auto-ordered: ${stainLabels}`}
                position="bottom"
                withArrow
                multiline
                maw={240}
              >
                <Group
                  gap={4}
                  mt={4}
                  wrap="nowrap"
                  style={{ cursor: "default" }}
                >
                  <IconFlask
                    size={10}
                    color="var(--mantine-color-teal-6)"
                    style={{ flexShrink: 0 }}
                  />
                  <Text
                    size="xs"
                    c="teal"
                    fw={500}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {count} auto-ordered
                  </Text>
                </Group>
              </Tooltip>
            );
          })()}
        </Table.Td>

        {/* Diagnosis */}
        <Table.Td
          style={{
            verticalAlign: "middle",
            cursor: isReadOnly ? "default" : "pointer",
            backgroundColor:
              expandedField === "diagnosis"
                ? "var(--mantine-color-blue-0)"
                : undefined,
          }}
          onClick={handleDiagnosisCellClick}
        >
          {diagnosisPlain ? (
            <Text
              size="sm"
              lineClamp={2}
              title={diagnosisPlain}
              style={{ wordBreak: "break-word" }}
            >
              {diagnosisPlain}
            </Text>
          ) : (
            <Text size="sm" c="dimmed" fs="italic">
              —
            </Text>
          )}
        </Table.Td>

        {/* Microscopic Description */}
        <Table.Td
          style={{
            verticalAlign: "middle",
            cursor: isReadOnly ? "default" : "pointer",
            backgroundColor:
              expandedField === "microscopic"
                ? "var(--mantine-color-blue-0)"
                : undefined,
          }}
          onClick={handleMicroscopicCellClick}
        >
          {microscopicPlain ? (
            <Text
              size="sm"
              lineClamp={2}
              title={microscopicPlain}
              style={{ wordBreak: "break-word" }}
            >
              {microscopicPlain}
            </Text>
          ) : (
            <Text size="sm" c="dimmed" fs="italic">
              —
            </Text>
          )}
        </Table.Td>

        {/* Category */}
        <Table.Td style={{ verticalAlign: "middle" }}>
          {isReadOnly ? (
            <Text size="sm">{categoryLabel || "—"}</Text>
          ) : (
            <Select
              value={diagnosisData.category}
              onChange={handleCategoryChange}
              data={categoryOptions}
              placeholder="Category"
              size="xs"
              searchable
              clearable
              title={categoryLabel ?? undefined}
              comboboxProps={{
                width: 260,
                position: "bottom-start",
                withinPortal: true,
              }}
              styles={{ option: { whiteSpace: "normal" } }}
            />
          )}
        </Table.Td>

        {/* ICD-10 */}
        <Table.Td style={{ verticalAlign: "middle", maxWidth: 155 }}>
          {isReadOnly ? (
            <Text size="sm">
              {resolveIcdCodes(diagnosisData).join(", ") || "—"}
            </Text>
          ) : (
            <MultiSelect
              aria-label={`ICD-10 for specimen ${specimen.number}`}
              value={resolveIcdCodes(diagnosisData)}
              onChange={handleIcdCodesChange}
              data={icdSelectOptions}
              placeholder={hasAnyIcdCode(diagnosisData) ? undefined : "ICD-10"}
              size="xs"
              searchable
              clearable
              filter={filterIcdCodeOptions}
              renderOption={IcdCodeSelectOption}
              comboboxProps={{
                width: 380,
                position: "bottom-end",
                withinPortal: true,
              }}
              styles={{
                input: { fontFamily: "monospace", fontWeight: 600 },
                // Mantine's PillsInput field defaults to min-width: 100px,
                // which pushes this cell past its 155px column in the
                // auto-layout table and widens the whole ICD column. Let the
                // search field shrink so pills wrap inside the column instead.
                inputField: { minWidth: 24 },
              }}
            />
          )}
        </Table.Td>

        <Table.Td style={{ verticalAlign: "middle", textAlign: "center" }}>
          {canRequestStains && (
            <Tooltip
              label={`Request stains for specimen ${specimen.number}`}
              withArrow
            >
              <ActionIcon
                variant="subtle"
                color="teal"
                size="sm"
                aria-label={`Request stains for specimen ${specimen.number}`}
                onClick={handleRequestStains}
              >
                <IconFlask size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Table.Td>

        {/* Actions */}
        <Table.Td style={{ verticalAlign: "middle", textAlign: "center" }}>
          {canModifyStructure && onArchiveSpecimen && (
            <Tooltip
              label={
                canRemove ? "Remove specimen" : "A case must keep one specimen"
              }
              withArrow
            >
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                aria-label={`Remove specimen ${specimen.number}`}
                disabled={!canRemove}
                onClick={handleRemoveSpecimen}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Table.Td>
      </Table.Tr>

      {/* Inline editor expansion row */}
      {expandedField && (
        <Table.Tr ref={expansionRowRef}>
          <Table.Td colSpan={columnCount} style={{ padding: "12px 16px" }}>
            <RichTextDiagnosis
              label={
                expandedField === "diagnosis"
                  ? "Diagnosis"
                  : "Microscopic Description"
              }
              value={
                expandedField === "diagnosis"
                  ? diagnosisData.diagnosisText || ""
                  : diagnosisData.microscopicDescription || ""
              }
              onChange={(val) =>
                onDiagnosisChange(
                  expandedField === "diagnosis"
                    ? { diagnosisText: val }
                    : { microscopicDescription: val }
                )
              }
              disabled={isReadOnly}
              minHeight={120}
              autoFocus
              variableConfig={{
                LOCATION: {
                  options: subsiteOptions || [],
                  value:
                    diagnosisData.location ?? specimen.subsite?.name ?? null,
                  onChange: (val) => onDiagnosisChange({ location: val }),
                },
                location: {
                  options: subsiteOptions || [],
                  value:
                    diagnosisData.location ?? specimen.subsite?.name ?? null,
                  onChange: (val) => onDiagnosisChange({ location: val }),
                },
                specimen_letter: {
                  options: [],
                  value: specimen.number ?? null,
                  onChange: () => {},
                  readOnly: true,
                },
                source: {
                  options: [],
                  value: specimen.source?.name ?? null,
                  onChange: () => {},
                  readOnly: true,
                },
                subsite: {
                  options: subsiteOptions || [],
                  value:
                    diagnosisData.location ?? specimen.subsite?.name ?? null,
                  onChange: (val) => onDiagnosisChange({ location: val }),
                },
                collection_method: {
                  options: [],
                  value: specimen.collectionMethod ?? null,
                  onChange: () => {},
                  readOnly: true,
                },
                block_count: {
                  options: [],
                  value:
                    specimen.blocks?.length != null
                      ? String(specimen.blocks.length)
                      : null,
                  onChange: () => {},
                  readOnly: true,
                },
                case_number: {
                  options: [],
                  value: caseInfo?.caseNumber ?? null,
                  onChange: () => {},
                  readOnly: true,
                },
                patient_name: {
                  options: [],
                  value: caseInfo?.patientName ?? null,
                  onChange: () => {},
                  readOnly: true,
                },
                date_collected: {
                  options: [],
                  value: caseInfo?.dateCollected ?? null,
                  onChange: () => {},
                  readOnly: true,
                },
                color: {
                  options: [],
                  value: diagnosisData.color ?? null,
                  onChange: (val) => onDiagnosisChange({ color: val }),
                },
                size_cm: {
                  options: [],
                  value: diagnosisData.sizeCm ?? null,
                  onChange: (val) => onDiagnosisChange({ sizeCm: val }),
                },
                sizeCm: {
                  options: [],
                  value: diagnosisData.sizeCm ?? null,
                  onChange: (val) => onDiagnosisChange({ sizeCm: val }),
                },
                size_mm: {
                  options: [],
                  value: diagnosisData.sizeMm ?? null,
                  onChange: (val) => onDiagnosisChange({ sizeMm: val }),
                },
                consistency: {
                  options: [],
                  value: diagnosisData.consistency ?? null,
                  onChange: (val) => onDiagnosisChange({ consistency: val }),
                },
              }}
            />
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

export function DiagnosisEntryGrid({
  specimens,
  diagnosisDataMap,
  onDiagnosisChange,
  onOpenMacroSearch,
  diagnosisMacros,
  categoryOptions,
  icdCodeOptions,
  isReadOnly,
  caseInfo,
  canModifyStructure,
  canAddSpecimen,
  onArchiveSpecimen,
  onAddSpecimen,
  onRecoverSpecimens,
  archivedCount,
  onOpenMacroSearchBar,
  canRequestAdditionalStains,
  onRequestStains,
}: DiagnosisEntryGridProps) {
  const columnCount = 8;
  const requestStainsEnabled = useMemo(
    () => canRequestAdditionalStains ?? !isReadOnly,
    [canRequestAdditionalStains, isReadOnly]
  );

  // A case must keep at least one specimen, so removal is only offered when
  // more than one remains.
  const canRemoveSpecimen =
    (canModifyStructure ?? false) && specimens.length > 1;

  const icdSelectOptions = useMemo<IcdCodeComboboxItem[]>(
    () =>
      icdCodeOptions.map((option) => ({
        value: option.value,
        label: option.value,
        fullLabel: option.label,
      })),
    [icdCodeOptions]
  );

  // One ref per specimen row, keyed by specimen id.
  // These are passed to each DiagnosisRow so the grid can focus any input directly.
  const inputRefsMap = useRef<Map<string, React.RefObject<HTMLInputElement>>>(
    new Map()
  );
  specimens.forEach((s: any) => {
    if (!inputRefsMap.current.has(s.id)) {
      inputRefsMap.current.set(s.id, { current: null });
    }
  });

  // Initial focus lands on the first Dx Code field still awaiting a code, so the
  // pathologist can type straight away instead of clicking into the grid on
  // every case. Switching to the Table view remounts this component, so a
  // mount-time effect covers both opening a case and activating the tab.
  const hasFocusedInitialDxCode = useRef(false);

  const focusFirstEmptyDxCode = useCallback((): boolean => {
    const firstEmpty = specimens.find((s: any) => {
      const data = diagnosisDataMap.get(s.id);
      return !data?.macroCode?.trim();
    });
    const target = firstEmpty ?? specimens[0];
    if (!target) return false;
    const input = inputRefsMap.current.get(target.id)?.current;
    if (!input) return false;
    input.focus();
    return true;
  }, [specimens, diagnosisDataMap]);

  useEffect(() => {
    if (isReadOnly || hasFocusedInitialDxCode.current) return;
    if (specimens.length === 0) return;

    let frameId = 0;
    const attemptFocus = (remainingFrames: number) => {
      if (focusFirstEmptyDxCode()) {
        hasFocusedInitialDxCode.current = true;
        return;
      }
      if (remainingFrames > 0) {
        frameId = requestAnimationFrame(() =>
          attemptFocus(remainingFrames - 1)
        );
      }
    };
    frameId = requestAnimationFrame(() =>
      attemptFocus(INITIAL_FOCUS_RETRY_FRAMES)
    );

    return () => cancelAnimationFrame(frameId);
  }, [isReadOnly, specimens.length, focusFirstEmptyDxCode]);

  // Compute missing fields summary for all specimens, splitting diagnosis
  // errors (red) from other incomplete fields (orange)
  const { diagnosisErrors, incompleteSpecimens } = useMemo(() => {
    const diagnosisErrors: Array<{ specimenNumber: string }> = [];
    const incompleteSpecimens: Array<{
      specimenNumber: string;
      missing: string[];
    }> = [];

    specimens.forEach((specimen: any) => {
      const diagnosisData = diagnosisDataMap.get(specimen.id) || {
        specimenId: specimen.id,
        macroCode: "",
        diagnosisText: "",
        microscopicDescription: "",
        location: null,
        category: null,
        cptCode: null,
        icdCode: null,
        icdCodes: [],
        color: null,
        sizeCm: null,
        sizeMm: null,
        consistency: null,
      };
      const missing = getMissingFields(diagnosisData);
      const hasAnyData =
        !isEmptyHtml(diagnosisData.diagnosisText) ||
        !!diagnosisData.category ||
        !!diagnosisData.cptCode ||
        hasAnyIcdCode(diagnosisData);

      if (hasAnyData && missing.includes("Diagnosis")) {
        diagnosisErrors.push({
          specimenNumber: specimen.number || specimen.id,
        });
      }

      const otherMissing = missing.filter((f) => f !== "Diagnosis");
      if (hasAnyData && otherMissing.length > 0) {
        incompleteSpecimens.push({
          specimenNumber: specimen.number || specimen.id,
          missing: otherMissing,
        });
      }
    });

    return { diagnosisErrors, incompleteSpecimens };
  }, [specimens, diagnosisDataMap]);

  return (
    <Box>
      {(diagnosisErrors.length > 0 || incompleteSpecimens.length > 0) && (
        <Group gap="sm" mb="sm" align="flex-start" wrap="wrap">
          {diagnosisErrors.length > 0 && (
            <Alert
              color="red"
              variant="light"
              icon={<IconAlertCircle size={16} />}
              title="Missing Diagnosis"
              p="xs"
              style={{ flexShrink: 0 }}
              styles={{ title: { fontSize: "var(--mantine-font-size-xs)" } }}
            >
              {diagnosisErrors.map((s) => (
                <Text key={s.specimenNumber} size="xs">
                  <Text span fw={600}>
                    Specimen {s.specimenNumber}:
                  </Text>{" "}
                  Diagnosis text is required
                </Text>
              ))}
            </Alert>
          )}
          {incompleteSpecimens.length > 0 && (
            <Alert
              color="orange"
              variant="light"
              icon={<IconAlertTriangle size={16} />}
              title="Incomplete Specimens"
              p="xs"
              style={{ flexShrink: 0 }}
              styles={{ title: { fontSize: "var(--mantine-font-size-xs)" } }}
            >
              {incompleteSpecimens.map((s) => (
                <Text key={s.specimenNumber} size="xs">
                  <Text span fw={600}>
                    Specimen {s.specimenNumber}:
                  </Text>{" "}
                  No {s.missing.join(", no ")}
                </Text>
              ))}
            </Alert>
          )}
        </Group>
      )}
      <Group justify="flex-end" gap="xs" mb="sm">
        {/* Click-only trigger for the macro search modal. A read-only input is
            still a tab stop, so it is removed from the tab sequence — otherwise
            tabbing lands in a box that cannot be typed into. F3 opens the same
            search from anywhere on the page. */}
        <TextInput
          placeholder="Search macros..."
          leftSection={<IconSearch size={14} />}
          rightSection={<Kbd size="xs">F3</Kbd>}
          readOnly
          size="xs"
          style={{ cursor: "pointer" }}
          styles={{ input: { cursor: "pointer", fontSize: 12 } }}
          w={200}
          onClick={onOpenMacroSearchBar}
          tabIndex={-1}
        />
        {canModifyStructure && canAddSpecimen !== false && (
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={onAddSpecimen}
          >
            Add specimen
          </Button>
        )}
        {canModifyStructure && (archivedCount ?? 0) > 0 && (
          <Button
            size="xs"
            variant="light"
            color="orange"
            leftSection={<IconRestore size={14} />}
            onClick={onRecoverSpecimens}
          >
            Recover ({archivedCount})
          </Button>
        )}
      </Group>
      <Table.ScrollContainer minWidth={1100}>
        <Table
          withTableBorder
          withColumnBorders
          highlightOnHover
          verticalSpacing="xs"
          horizontalSpacing="xs"
          styles={{
            th: {
              whiteSpace: "nowrap",
              fontSize: "var(--mantine-font-size-xs)",
              fontWeight: 600,
              textTransform: "uppercase",
              color: "var(--mantine-color-dimmed)",
            },
          }}
        >
          <colgroup>
            <col style={{ width: 110 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "30%" }} />
            <col style={{ width: 200 }} />
            <col style={{ width: 155 }} />
            <col style={{ width: 44 }} />
            <col style={{ width: 48 }} />
          </colgroup>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Specimen</Table.Th>
              <Table.Th>Dx Code</Table.Th>
              <Table.Th>Diagnosis</Table.Th>
              <Table.Th>Microscopic Desc</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>ICD-10</Table.Th>
              <Table.Th>
                <VisuallyHidden>Request stains</VisuallyHidden>
              </Table.Th>
              <Table.Th>
                <VisuallyHidden>Actions</VisuallyHidden>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {specimens.map((specimen: any, index: number) => {
              const diagnosisData = diagnosisDataMap.get(specimen.id) || {
                specimenId: specimen.id,
                macroCode: "",
                diagnosisText: "",
                microscopicDescription: "",
                location: null,
                category: null,
                cptCode: null,
                icdCode: null,
                icdCodes: [],
                color: null,
                sizeCm: null,
                sizeMm: null,
                consistency: null,
              };

              const inputRef = inputRefsMap.current.get(specimen.id)!;

              // Find the next specimen after this one whose Dx Code is empty,
              // then focus its input directly via the ref map.
              // If no empty field exists, blur the current input to dismiss
              // the autocomplete dropdown.
              const handleFocusNextMacro = () => {
                const remaining = specimens.slice(index + 1);
                const nextEmpty = remaining.find((s: any) => {
                  const ref = inputRefsMap.current.get(s.id);
                  return ref?.current && !ref.current.value.trim();
                });
                if (nextEmpty) {
                  const ref = inputRefsMap.current.get(nextEmpty.id);
                  ref?.current?.focus();
                } else {
                  inputRef.current?.blur();
                }
              };

              return (
                <DiagnosisRow
                  key={specimen.id}
                  specimen={specimen}
                  diagnosisData={diagnosisData}
                  inputRef={inputRef}
                  onDiagnosisChange={(data) =>
                    onDiagnosisChange(specimen.id, data)
                  }
                  onOpenMacroSearch={() => onOpenMacroSearch(specimen.id)}
                  diagnosisMacros={diagnosisMacros}
                  categoryOptions={categoryOptions}
                  icdSelectOptions={icdSelectOptions}
                  isReadOnly={isReadOnly}
                  columnCount={columnCount}
                  onFocusNextMacro={handleFocusNextMacro}
                  caseInfo={caseInfo}
                  canModifyStructure={canModifyStructure}
                  canRemove={canRemoveSpecimen}
                  onArchiveSpecimen={onArchiveSpecimen}
                  canRequestAdditionalStains={requestStainsEnabled}
                  onRequestStains={onRequestStains}
                />
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Box>
  );
}
